import { Button, Menu, Popover, rem, Slider } from '@mantine/core';
import { modals } from '@mantine/modals';
import { formatKm, holdsPlace, minutesToHm, photoUrl, isExternal, ROUTE_WEIGHT, zonedNow, type LiveShiftView, type LiveStatus } from '@reditelji/shared';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import { Link } from 'react-router';
import {
  useCheckinMutations,
  useChangelog,
  useEvent,
  useRaceRoutes,
  useRouteMutations,
  useTimeline,
  type RoutePatch,
} from '../api/hooks';
import { usePosts } from '../api/posts';
import { useSession } from '../api/session';
import type { EventDay, RaceRoute, Sector, Shift, TimelinePost } from '../api/types';
import { useNow } from '../live/clock';
import { checkinActions, checkinsByShift, liveChip, type ShiftCheckins } from '../live/derive';
import { dayPhase } from '../lib/ui';
import { blockStatus, briefingStale, postRunsOnDay } from '../timeline/derive';
import { makeAxis } from '../timeline/geometry';
import { ROUTE_PALETTE, RouteImportModal } from './RouteImportModal';
import { describeRouteRef, isOnCourse, routeRefsByPost, type PostRouteRef } from './routeRefs';
import { useUi } from '../state/ui';
import {
  atTime,
  briefMissing,
  changeLabel,
  externalOn,
  LIVE_COL,
  LIVE_LEGEND,
  legendKey,
  liveViewsOf,
  livePartsOf,
  MAP_COL,
  pinFill,
  MAP_LEGEND,
  recentChangesByPost,
  statusOf,
  type MapStatusKey,
  type PostChange,
  type VolunteerLookup,
} from './derive';
import './map.css';

const HOME: [number, number] = [46.43, 13.95];

type Basemap = 'osm' | 'satelit';

const BASEMAPS: Record<Basemap, { label: string; source: string; url: string; attribution: string }> = {
  osm: {
    label: 'Zemljevid',
    source: 'OpenStreetMap',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
  },
  satelit: {
    label: 'Satelit',
    source: 'Esri World Imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Esri, Maxar, Earthstar Geographics, GIS User Community',
  },
};

/** A course's colour or weight while it is being picked, before it is saved. */
type RouteStyleDraft = Record<string, { color?: string; weight?: number }>;

interface DayLayers {
  gaps: boolean;
  brief: boolean;
  trasa: boolean;
  parking: boolean;
  sce: boolean;
}

const DEFAULT_LAYERS: DayLayers = { gaps: true, brief: false, trasa: true, parking: true, sce: true };

const LAYER_ROWS: { key: keyof DayLayers; label: string; sub: string }[] = [
  { key: 'gaps', label: 'Vrzeli in odpovedi', sub: 'opozorila' },
  { key: 'brief', label: 'Manjkajoča navodila', sub: 'komunikacija' },
  { key: 'trasa', label: 'Mesta na trasi', sub: 'tip mesta' },
  { key: 'parking', label: 'Parkirišča', sub: 'tip mesta' },
  { key: 'sce', label: 'Štartno-ciljni prostor', sub: 'tip mesta' },
];

/** Grabs the Leaflet map instance for imperative moves (fit, fly-to). */
function MapRef({ mapRef }: { mapRef: React.RefObject<L.Map | null> }) {
  mapRef.current = useMap();
  return null;
}

export function MapPage() {
  const { selectedDayId, setSelectedDay } = useUi();
  const event = useEvent();
  const timeline = useTimeline(selectedDayId);
  // Only for the cover thumbnail on the detail card — the timeline payload
  // carries no photos, and this query is already in the cache from Mesta.
  const posts = usePosts();
  const routes = useRaceRoutes();
  const changelog = useChangelog();
  const { can } = useSession();
  const mapRef = useRef<L.Map | null>(null);

  const [layersByDay, setLayersByDay] = useState<Record<string, DayLayers>>({});
  /** Explicit show/hide per route; without an entry the day binding decides. */
  const [routeOverride, setRouteOverride] = useState<Record<string, boolean>>({});
  const [legendOff, setLegendOff] = useState<Partial<Record<MapStatusKey, boolean>>>({});
  const [live, setLive] = useState(false);
  const [time, setTime] = useState(860);
  /** On race day the time view follows the clock until the slider is moved. */
  const [follow, setFollow] = useState(true);
  const [liveOff, setLiveOff] = useState<Partial<Record<LiveStatus, boolean>>>({});
  const clock = useNow();

  const [labels, setLabels] = useState(true);
  const [calm, setCalm] = useState(true);
  const [basemap, setBasemap] = useState<Basemap>('osm');
  const [styleDraft, setStyleDraft] = useState<RouteStyleDraft>({});
  const [sel, setSel] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState<'sloji' | 'trase' | 'splosno'>('sloji');
  const [q, setQ] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  /** Set when the modal is opened from a course's "Zamenjaj z GPX…". */
  const [replacing, setReplacing] = useState<RaceRoute | null>(null);

  const data = timeline.data;
  // The same hours the board draws for this day, so the two agree about when
  // the day starts — Sunday's begins at midnight.
  const axis = useMemo(
    () => makeAxis((data?.posts ?? []).flatMap((p) => [...p.windows, ...p.shifts])),
    [data?.posts],
  );
  const phase = data ? dayPhase(data.day.date, clock.isoDate) : 'next';
  // Race day opens on the live view (SPEC §1.2).
  useEffect(() => {
    if (phase === 'live') {
      setLive(true);
      setFollow(true);
    }
  }, [phase]);
  /**
   * The minute the time view shows: the clock's today, and in the small hours
   * the end of the day just over, for as long as nobody has moved the slider.
   * Any other day stands at the slider's minute.
   */
  const justOver = phase === 'past' && data !== undefined && nextIsoDate(data.day.date) === clock.isoDate && clock.minutes < 360;
  const minute =
    live && follow && (phase === 'live' || justOver) ? (phase === 'live' ? clock.minutes : Math.min(1440, axis.toMin)) : time;
  // When the small hours end on a day still being followed, it stays at its
  // end rather than falling back to wherever the slider last stood.
  const followedDay = useRef<{ dayId: string | undefined; justOver: boolean }>({ dayId: undefined, justOver: false });
  useEffect(() => {
    const prev = followedDay.current;
    const dayId = data?.day.id;
    // Only the same day crossing out of the small hours — not a switch to another day.
    if (prev.dayId === dayId && prev.justOver && !justOver && live && follow) {
      setTime(Math.min(1440, axis.toMin));
      setFollow(false);
    }
    followedDay.current = { dayId, justOver };
  }, [live, follow, justOver, data?.day.id, axis.toMin]);
  /**
   * Who is on each post by check-ins — today, and on a day that is over. A day
   * still ahead has no check-ins, so its time view keeps the planning colours.
   */
  const raceView = live && phase !== 'next';
  const cmAll = useMemo(() => checkinsByShift(data?.checkins ?? []), [data?.checkins]);
  const dayLayers = (selectedDayId && layersByDay[selectedDayId]) || DEFAULT_LAYERS;
  const setDayLayer = (key: keyof DayLayers, on: boolean) => {
    if (!selectedDayId) return;
    setLayersByDay((prev) => ({ ...prev, [selectedDayId]: { ...dayLayers, [key]: on } }));
  };

  /**
   * A course is shown when it runs on the selected day; one bound to no day
   * runs throughout. A manual toggle overrides that for the session, so a
   * coordinator can still pull up another day's course for comparison.
   */
  const runsOnSelectedDay = (r: RaceRoute) =>
    r.eventDayIds.length === 0 || (selectedDayId !== null && r.eventDayIds.includes(selectedDayId));
  const routeVisible = (r: RaceRoute) => routeOverride[r.id] ?? runsOnSelectedDay(r);
  const toggleRoute = (r: RaceRoute) =>
    setRouteOverride((prev) => ({ ...prev, [r.id]: !(prev[r.id] ?? runsOnSelectedDay(r)) }));

  const vols: VolunteerLookup = useMemo(
    () => new Map((data?.volunteers ?? []).map((v) => [v.id, v])),
    [data?.volunteers],
  );
  const now = useMemo(() => new Date(), []);
  const changes = useMemo(
    () => recentChangesByPost(changelog.data?.entries ?? [], data?.posts ?? [], now),
    [changelog.data?.entries, data?.posts, now],
  );

  /**
   * The check-ins made by the minute shown: moved back to 08:00, a shift that
   * checked out at 20:00 has not yet. Read from when each was recorded, in
   * Ljubljana — the minute a check-in carries starts over at midnight.
   * Following today's clock, all of them.
   */
  const cm = useMemo((): Map<string, ShiftCheckins> => {
    if (live && follow && phase === 'live') return cmAll;
    const date = data?.day.date ?? '';
    const madeBy = (row: { createdAt: string }) => {
      const at = zonedNow(new Date(row.createdAt));
      // At the day's end — the slider's last stop — everything recorded about it counts — a check-out
      // after midnight, an arrival a coordinator entered the next morning.
      return minute >= Math.min(1440, axis.toMin) || at.isoDate < date || (at.isoDate === date && at.minutes <= minute);
    };
    // Every row made by then, not only the latest: an arrival recorded twice
    // was still an arrival at the first.
    return checkinsByShift((data?.checkins ?? []).filter(madeBy));
  }, [cmAll, live, follow, phase, minute, data?.day.date, data?.checkins, axis.toMin]);
  const keyOf = (p: TimelinePost): MapStatusKey =>
    live ? atTime(p, minute, vols).key : statusOf(p, vols);
  const partsOf = (p: TimelinePost) => livePartsOf(p, cm, vols, minute);
  /**
   * Does an outside group work here? In time-slider mode this follows the
   * minute, the same as the pin's colour — otherwise a post whose team starts at
   * 14:00 would carry the square at 06:00 and claim something untrue.
   */
  const externalMark = (p: TimelinePost) => externalOn(p, vols, live ? minute : undefined);

  // Keep the state on the axis, not just the thumb: `time` also decides which
  // status colour every pin gets, so a value off the axis would paint the map
  // for a minute the slider is not showing.
  useEffect(() => {
    setTime((m) => Math.min(axis.toMin, Math.max(axis.fromMin, m)));
  }, [axis]);

  // Only the posts that work on the selected day; the payload lists them all.
  const located = useMemo(
    () => (data?.posts ?? []).filter((p) => postRunsOnDay(p) && p.lat !== null && p.lng !== null),
    [data?.posts],
  );
  const visible = located.filter((p) => {
    if (p.type === 'parkirišče' && !dayLayers.parking) return false;
    if (p.type === 'štartno-ciljni' && !dayLayers.sce) return false;
    if (p.type === 'trasa' && !dayLayers.trasa) return false;
    if (raceView) {
      // Hidden when every person who matters there now is in a hidden row.
      const parts = partsOf(p);
      // A post whose day is done has nobody to hide it by.
      return parts.length === 0 || parts.some((x) => !liveOff[legendKey(x.status)]);
    }
    const key = keyOf(p);
    if (key === 'gap' && !dayLayers.gaps) return false;
    if (legendOff[key]) return false;
    // 'ekipa' is a marker rather than a pin colour, so it is never `keyOf`'s
    // answer — its legend row has to filter on the marker itself or clicking it
    // would dim the row and hide nothing.
    if (legendOff.ekipa && externalMark(p)) return false;
    return true;
  });

  const fit = () => {
    const map = mapRef.current;
    if (!map) return;
    if (!visible.length) {
      map.setView(HOME, 11);
      return;
    }
    map.fitBounds(L.latLngBounds(visible.map((p) => [p.lat!, p.lng!])).pad(0.18), {
      animate: true,
    });
  };

  const countKey = (k: MapStatusKey) => {
    if (k === 'brief') return located.filter((p) => briefMissing(p, vols)).length;
    if (k === 'ekipa') return located.filter(externalMark).length;
    return located.filter((p) => keyOf(p) === k).length;
  };

  const selPost = sel ? (located.find((p) => p.id === sel) ?? null) : null;

  const liveParts = raceView ? located.map((p) => ({ post: p, parts: partsOf(p) })) : [];
  const liveCount = (k: LiveStatus) =>
    liveParts.reduce((n, x) => n + x.parts.filter((part) => legendKey(part.status) === k).length, 0);
  /** Worst first: nobody on it, late, never came, not checked in yet. */
  const attention = liveParts
    .flatMap(({ post, parts }) => parts.map((part) => ({ post, ...part })))
    .filter((x) => x.status === 'prosto' || x.status === 'zamuja' || x.status === 'brez' || x.status === 'ni')
    .sort((a, b) => LIVE_RANK_ORDER.indexOf(a.status) - LIVE_RANK_ORDER.indexOf(b.status) || a.shift.fromMin - b.shift.fromMin);

  // Kilometre mark per post, derived from the imported courses.
  const routeRefs = useMemo(
    () => routeRefsByPost(located, routes.data ?? []),
    [located, routes.data],
  );

  const sectorHulls: { sector: Sector; count: number }[] = (() => {
    const byId = new Map<string, { sector: Sector; count: number }>();
    if (!data) return [];
    const sectors = new Map(data.sectors.map((s) => [s.id, s]));
    for (const p of visible) {
      const s = sectors.get(p.sectorId);
      if (!s || s.lat === null || s.lng === null) continue;
      const cur = byId.get(s.id);
      if (cur) cur.count += 1;
      else byId.set(s.id, { sector: s, count: 1 });
    }
    return [...byId.values()];
  })();

  const legendRows: [MapStatusKey, string][] = live
    ? [...MAP_LEGEND.filter(([k]) => k !== 'brief'), ['off', 'zunaj izmene']]
    : can('comms:read')
      ? MAP_LEGEND
      : MAP_LEGEND.filter(([k]) => k !== 'brief');

  const hits = q.trim()
    ? located
        .filter((p) => `${p.code} ${p.micro}`.toLowerCase().includes(q.trim().toLowerCase()))
        .slice(0, 12)
    : [];

  return (
    <div className="mp-stage">
      {/* Muting is for the drawn map; a muted photograph only loses detail. */}
      <div
        className={`mp-map${basemap === 'satelit' ? ' mp-sat' : calm ? ' mp-calm' : ''}`}
      >
        <MapContainer center={HOME} zoom={11} zoomControl={false} className="mp-leaflet">
          <MapRef mapRef={mapRef} />
          <TileLayer
            // Attribution is read once, so a new base map is a new layer.
            key={basemap}
            url={BASEMAPS[basemap].url}
            maxZoom={18}
            attribution={BASEMAPS[basemap].attribution}
          />
          {(routes.data ?? []).filter(routeVisible).map((r) => (
            <RoutePolylines key={r.id} route={{ ...r, ...styleDraft[r.id] }} />
          ))}
          {sectorHulls.map(({ sector, count }) => (
            <SectorHull key={sector.id} sector={sector} count={count} />
          ))}
          {visible.map((p) => (
            <PostPin
              key={p.id}
              post={p}
              statusKey={keyOf(p)}
              fill={raceView ? pinFill(partsOf(p)) : null}
              dashed={raceView && partsOf(p).some((x) => x.status === 'prosto')}
              selected={sel === p.id}
              showLabel={labels}
              briefLayer={dayLayers.brief}
              briefIsMissing={briefMissing(p, vols)}
              external={externalMark(p)}
              change={changes.get(p.id) ?? null}
              now={now}
              onClick={() => setSel(p.id)}
            />
          ))}
        </MapContainer>
      </div>

      <div className="mp-float mp-search">
        <input
          placeholder="Išči oznako ali lokacijo…"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        {hits.length > 0 || q.trim() ? (
          <div className="mp-results">
            {hits.length ? (
              hits.map((p) => (
                <div
                  key={p.id}
                  onClick={() => {
                    setSel(p.id);
                    mapRef.current?.setView([p.lat!, p.lng!], 15, { animate: true });
                    setQ('');
                  }}
                >
                  <b className="mp-mono" style={{ fontSize: rem(12) }}>
                    {p.code}
                  </b>{' '}
                  <span style={{ color: 'oklch(0.5 0.012 90)' }}>{p.micro}</span>
                </div>
              ))
            ) : (
              <div style={{ color: 'oklch(0.6 0.012 90)' }}>Ni zadetkov.</div>
            )}
          </div>
        ) : null}
      </div>

      <div className="mp-float mp-toolbar">
        <button className="mp-ghost" onClick={fit}>
          Prilagodi pogled
        </button>
        <button className="mp-ghost" onClick={() => setPanelOpen(true)}>
          Sloji
        </button>
      </div>

      <div className="mp-float mp-legend">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <span style={{ fontSize: rem(12), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}>
            LEGENDA
          </span>
          <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
            {live ? `stanje ob ${minutesToHm(minute)}` : (data?.day.label ?? '')}
          </span>
        </div>
        {raceView
          ? LIVE_LEGEND.map(([k, label]) => (
              <div
                key={k}
                className={`mp-lrow${liveOff[k] ? ' mp-off' : ''}`}
                onClick={() => setLiveOff((prev) => ({ ...prev, [k]: !prev[k] }))}
              >
                <span
                  className="mp-lpin"
                  style={
                    k === 'prosto'
                      ? { background: 'oklch(0.96 0.03 25)', outline: `1.5px dashed ${LIVE_COL.prosto}` }
                      : { background: LIVE_COL[k] }
                  }
                >
                  {k === 'ekipa' ? <span className="mp-lsq" /> : null}
                </span>
                <span style={{ flex: 1 }}>{label}</span>
                <span className="mp-mono" style={{ fontSize: rem(11), color: 'oklch(0.6 0.012 90)' }}>
                  {liveCount(k)}
                </span>
              </div>
            ))
          : null}
        {(raceView ? [] : legendRows).map(([k, label]) => {
          const dim = k === 'brief' ? !dayLayers.brief : Boolean(legendOff[k]);
          return (
            <div
              key={k}
              className={`mp-lrow${dim ? ' mp-off' : ''}`}
              onClick={() => {
                if (k === 'brief') setDayLayer('brief', !dayLayers.brief);
                else setLegendOff((prev) => ({ ...prev, [k]: !prev[k] }));
              }}
            >
              <span className="mp-lpin" style={{ background: MAP_COL[k] }}>
                {k === 'ekipa' ? <span className="mp-lsq" /> : null}
              </span>
              <span style={{ flex: 1 }}>{label}</span>
              <span className="mp-mono" style={{ fontSize: rem(11), color: 'oklch(0.6 0.012 90)' }}>
                {countKey(k)}
              </span>
            </div>
          );
        })}
        <div className="mp-lhint">
          {raceView
            ? 'Zatič je razdeljen po osebah, ki so zdaj pomembne za mesto. Obroč = nujno mesto, kvadratek = zunanja ekipa.'
            : 'Klikni vrstico za skrivanje te skupine. Obroč okoli zatiča = nujno mesto.'}
        </div>
      </div>

      <div className="mp-float mp-timebar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
          <button
            className={`mp-chip${live ? ' mp-on' : ''}`}
            onClick={() => {
              setLive(!live);
              setFollow(true);
            }}
          >
            {phase === 'live' ? 'V živo' : 'Pogled po urah'}
          </button>
          <span className="mp-mono" style={{ fontSize: rem(13), fontWeight: 600 }}>
            {live ? minutesToHm(minute) : '—'}
          </span>
          {live && (phase === 'live' || justOver) && !follow ? (
            <button className="mp-chip" onClick={() => setFollow(true)}>
              Na zdaj
            </button>
          ) : null}
          <span style={{ fontSize: rem(12), color: 'oklch(0.58 0.012 90)' }}>
            {raceView
              ? `${liveCount('na')} na mestu · ${liveCount('zamuja') + liveCount('ni')} brez prijave · ${liveCount('prosto')} prostih`
              : live
                ? `${located.filter((p) => ['ok', 'part'].includes(atTime(p, minute, vols).key)).length} mest zasedenih · ${located.filter((p) => atTime(p, minute, vols).key === 'gap').length} prostih`
                : ''}
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
            {raceView
              ? follow && phase === 'live'
                ? 'barve kažejo prijave — sledi uri'
                : 'barve kažejo prijave ob tej uri'
              : live
              ? 'barve kažejo, kdo je na mestu ob tej uri'
              : 'barve kažejo pokritost celotnega dneva'}
          </span>
        </div>
        <input
          type="range"
          min={axis.fromMin}
          max={axis.toMin}
          step={15}
          value={minute}
          disabled={!live}
          onChange={(e) => {
            setTime(Number(e.currentTarget.value));
            setFollow(false);
          }}
        />
      </div>

      {/* Out of the way while a search lists its results in the same column. */}
      {raceView && !q.trim() ? (
        <div className="mp-float mp-attention">
          <div className="mp-att-head">
            POZORNOST ZDAJ <span className="mp-mono">{attention.length}</span>
          </div>
          {attention.length === 0 ? (
            <div className="mp-att-empty">Vsi so na mestu.</div>
          ) : (
            attention.map((x) => (
              <div
                key={x.shift.id}
                className="mp-att-row"
                onClick={() => {
                  setSel(x.post.id);
                  mapRef.current?.setView([x.post.lat!, x.post.lng!], 15, { animate: true });
                }}
              >
                <span
                  className="mp-att-dot"
                  style={
                    x.status === 'prosto'
                      ? { background: 'oklch(0.96 0.03 25)', outline: `1.5px dashed ${LIVE_COL.prosto}` }
                      : { background: LIVE_COL[x.status] }
                  }
                />
                <span className="mp-att-main">
                  <b>{holdsPlace(x.shift) ? (vols.get(x.shift.volunteerId!)?.name ?? '?') : 'PROSTO'}</b>
                  <span className="mp-mono">
                    {x.post.code} · {minutesToHm(x.shift.fromMin)}–{minutesToHm(x.shift.toMin)}
                  </span>
                </span>
                {x.status === 'prosto' ? null : (
                  <span className={`mp-att-chip mp-att-chip--${x.status}`}>
                    {liveChip(x.shift, cm, minute, false, liveViewsOf(x.post, cm, minute).get(x.shift.id))?.text ?? ''}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      ) : null}

      {selPost ? (
        <PostDetail
          post={selPost}
          statusKey={keyOf(selPost)}
          live={raceView ? { cm, minute, now: phase === 'live' && follow } : null}
          vols={vols}
          sectorName={data?.sectors.find((s) => s.id === selPost.sectorId)?.name ?? ''}
          coverKey={
            (posts.data ?? []).find((p) => p.id === selPost.id)?.photos[0]?.key ?? null
          }
          routeRef={routeRefs.get(selPost.id) ?? null}
          onClose={() => setSel(null)}
        />
      ) : null}

      <div className={`mp-panel${panelOpen ? ' mp-open' : ''}`}>
        <div className="mp-phead">
          <span style={{ fontSize: rem(14.5), fontWeight: 700, flex: 1 }}>Sloji in nastavitve</span>
          <button className="mp-x" onClick={() => setPanelOpen(false)}>
            ×
          </button>
        </div>
        <div className="mp-ptabs">
          {(
            [
              ['sloji', 'Sloji'],
              ['trase', 'Trase'],
              ['splosno', 'Splošno'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              className={panelTab === k ? 'mp-on' : ''}
              onClick={() => setPanelTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mp-pbody">
          {panelTab === 'sloji' ? (
            <LayersTab
              days={event.data?.days ?? []}
              selectedDayId={selectedDayId}
              layersByDay={layersByDay}
              onSelectDay={(id) => {
                setSelectedDay(id);
                setSel(null);
              }}
              onToggle={(dayId, key) =>
                setLayersByDay((prev) => {
                  const cur = prev[dayId] ?? DEFAULT_LAYERS;
                  return { ...prev, [dayId]: { ...cur, [key]: !cur[key] } };
                })
              }
            />
          ) : panelTab === 'trase' ? (
            <TraseTab
              routes={routes.data ?? []}
              days={event.data?.days ?? []}
              isVisible={routeVisible}
              onToggle={toggleRoute}
              draft={styleDraft}
              onDraft={(id, style) =>
                setStyleDraft((prev) => ({ ...prev, [id]: { ...prev[id], ...style } }))
              }
              onSettled={(id, saved) =>
                setStyleDraft((prev) => {
                  const { [id]: current, ...rest } = prev;
                  // Only what this save wrote, and only while nothing newer has been picked.
                  const left = Object.fromEntries(
                    Object.entries(current ?? {}).filter(
                      ([k, v]) => saved[k as keyof typeof saved] !== v,
                    ),
                  );
                  return Object.keys(left).length ? { ...rest, [id]: left } : rest;
                })
              }
              onImport={(target) => {
                setReplacing(target ?? null);
                setImportOpen(true);
              }}
            />
          ) : (
            <>
              <div className="mp-layer mp-card">
                <div className="mp-lt">
                  <b>Podlaga</b>
                </div>
                <div className="mp-seg">
                  {(Object.keys(BASEMAPS) as Basemap[]).map((k) => (
                    <button
                      key={k}
                      className={`mp-chip${basemap === k ? ' mp-on' : ''}`}
                      aria-pressed={basemap === k}
                      onClick={() => setBasemap(k)}
                    >
                      {BASEMAPS[k].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mp-layer mp-card">
                <div className="mp-lt">
                  <b>Oznake mest ob zatičih</b>
                </div>
                <Switch on={labels} onClick={() => setLabels(!labels)} />
              </div>
              <div
                className="mp-layer mp-card"
                style={basemap === 'osm' ? undefined : { opacity: 0.45 }}
              >
                <div className="mp-lt">
                  <b>Umirjena podlaga (manj barv)</b>
                  {basemap === 'osm' ? null : <span>velja za zemljevid</span>}
                </div>
                <Switch on={calm} onClick={() => setCalm(!calm)} />
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: rem(12),
                  letterSpacing: '0.04em',
                  color: 'oklch(0.55 0.012 90)',
                }}
              >
                PODATKI
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: rem(12.5),
                  color: 'oklch(0.42 0.012 90)',
                  lineHeight: 1.6,
                }}
              >
                Podlaga: {BASEMAPS[basemap].source}
                <br />
                Koordinate mest: iz kartice redarskega mesta
              </div>
              <Link
                to="/casovnica"
                style={{ display: 'inline-block', marginTop: 12, fontSize: rem(12.5) }}
              >
                Nazaj na časovnico ↗
              </Link>
            </>
          )}
        </div>
      </div>

      <RouteImportModal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        routes={routes.data ?? []}
        posts={data?.posts ?? []}
        replacing={replacing}
        onImported={(bbox) => {
          if (!bbox || !mapRef.current) return;
          mapRef.current.fitBounds(
            L.latLngBounds([bbox[1], bbox[0]], [bbox[3], bbox[2]]).pad(0.1),
            { animate: true },
          );
        }}
      />
    </div>
  );
}

function LayersTab({
  days,
  selectedDayId,
  layersByDay,
  onSelectDay,
  onToggle,
}: {
  days: { id: string; label: string }[];
  selectedDayId: string | null;
  layersByDay: Record<string, DayLayers>;
  onSelectDay: (id: string) => void;
  onToggle: (dayId: string, key: keyof DayLayers) => void;
}) {
  // "Manjkajoča navodila" is send status (SPEC §1.1L).
  const { can } = useSession();
  const rows = LAYER_ROWS.filter((l) => l.key !== 'brief' || can('comms:read'));
  return (
    <>
      {days.map((d) => {
        const on = d.id === selectedDayId;
        const layers = layersByDay[d.id] ?? DEFAULT_LAYERS;
        const visCount = rows.filter((l) => layers[l.key]).length + (on ? 1 : 0);
        return (
          <div key={d.id} className="mp-group">
            <div className="mp-gh">
              <Switch on={on} onClick={() => onSelectDay(d.id)} />
              <span style={{ flex: 1, fontSize: rem(13), fontWeight: 600, letterSpacing: '0.02em' }}>
                {d.label.toUpperCase()}
              </span>
              <span className="mp-mono" style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>
                {visCount}/{rows.length + 1} vidnih
              </span>
            </div>
            {rows.map((l) => (
              <div key={l.key} className="mp-layer" style={on ? undefined : { opacity: 0.45 }}>
                <div className="mp-lt">
                  <b>{l.label}</b>
                  <span>{l.sub}</span>
                </div>
                <Switch on={layers[l.key]} onClick={() => onToggle(d.id, l.key)} />
              </div>
            ))}
          </div>
        );
      })}
      <div style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)', lineHeight: 1.45 }}>
        Vklop dneva preklopi tudi statistiko in časovni drsnik na ta dan.
      </div>
    </>
  );
}

/**
 * Trase panel: the courses drawn on the map, and where GPX files come in.
 * Import and management live here because this is where a coordinator already
 * goes to reason about courses.
 */
function TraseTab({
  routes,
  days,
  isVisible,
  onToggle,
  draft,
  onDraft,
  onSettled,
  onImport,
}: {
  routes: RaceRoute[];
  days: EventDay[];
  isVisible: (r: RaceRoute) => boolean;
  onToggle: (r: RaceRoute) => void;
  draft: RouteStyleDraft;
  /** A style being picked is drawn at once, ahead of its save. */
  onDraft: (id: string, style: RouteStyleDraft[string]) => void;
  /**
   * A save has landed or failed. The draft stays until then, so the line never
   * shows the old style while the save waits its turn behind another.
   */
  onSettled: (id: string, saved: RouteStyleDraft[string]) => void;
  /** Without a route this is a plain import; with one it replaces that course. */
  onImport: (replace?: RaceRoute) => void;
}) {
  const mut = useRouteMutations();
  const [renaming, setRenaming] = useState<string | null>(null);
  // Import, rename and delete are plan writes (SPEC §1.1L); showing a course stays everyone's.
  const { can } = useSession();
  const canEdit = can('plan:write');

  const dayLabel = (ids: string[]) =>
    ids.length === 0
      ? 'vsi dnevi'
      : ids.map((id) => days.find((d) => d.id === id)?.label ?? '?').join(', ');

  const confirmDelete = (r: RaceRoute) =>
    modals.openConfirmModal({
      // Above the layers panel (.mp-panel sits at z-index 1200).
      zIndex: 1400,
      title: 'Izbris trase',
      children: (
        <span style={{ fontSize: rem(13.5) }}>
          Trasa »{r.name}« bo odstranjena z zemljevida. Kilometraža mest, ki so bila vezana nanjo,
          se ne bo več prikazovala.
        </span>
      ),
      labels: { confirm: 'Izbriši', cancel: 'Prekliči' },
      confirmProps: { color: 'red' },
      onConfirm: () => mut.remove.mutate(r.id),
    });

  return (
    <>
      {canEdit ? (
        <Button size="compact-sm" fullWidth mb={8} onClick={() => onImport()}>
          Uvozi GPX
        </Button>
      ) : null}

      {routes.length === 0 ? (
        <div style={{ fontSize: rem(12.5), color: 'oklch(0.55 0.012 90)', lineHeight: 1.5 }}>
          Nobena trasa še ni uvožena. Naloži datoteke GPX s progami, da se pravi potek nariše na
          zemljevid.
        </div>
      ) : null}

      {routes.map((r) => (
        <div key={r.id} className="mp-layer mp-card">
          {canEdit ? (
            <RouteStylePicker
              route={{ ...r, ...draft[r.id] }}
              onDraft={(style) => onDraft(r.id, style)}
              onCommit={(patch) => {
                onDraft(r.id, patch);
                // The promise, not mutate's callback: that fires for the latest call only,
                // and a colour picked before the slider moved has to settle as well. The
                // hook reports a failure itself.
                void mut.update
                  .mutateAsync({ id: r.id, ...patch })
                  .catch(() => undefined)
                  .finally(() => onSettled(r.id, patch));
              }}
            />
          ) : (
            <RouteSwatch route={r} />
          )}
          <div className="mp-lt" style={{ minWidth: 0 }}>
            {renaming === r.id ? (
              <input
                autoFocus
                defaultValue={r.name}
                onBlur={(e) => {
                  const name = e.currentTarget.value.trim();
                  setRenaming(null);
                  if (name && name !== r.name) mut.update.mutate({ id: r.id, name });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') setRenaming(null);
                }}
                style={{
                  font: 'inherit',
                  fontWeight: 700,
                  width: '100%',
                  border: '1px solid oklch(0.85 0.01 95)',
                  borderRadius: 5,
                  padding: '1px 4px',
                }}
              />
            ) : (
              <b
                onDoubleClick={canEdit ? () => setRenaming(r.id) : undefined}
                title={canEdit ? 'Dvoklik za preimenovanje' : undefined}
              >
                {r.name}
              </b>
            )}
            <span>
              {r.source === 'placeholder'
                ? 'poenostavljena povezava'
                : `GPX · ${formatKm(r.lengthM)}`}{' '}
              · {dayLabel(r.eventDayIds)}
            </span>
          </div>
          <Switch on={isVisible(r)} onClick={() => onToggle(r)} />
          {canEdit ? (
            <Menu position="bottom-end" withinPortal zIndex={1400}>
              <Menu.Target>
                <button className="mp-x" aria-label={`Možnosti za ${r.name}`}>
                  ⋯
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onImport(r)}>
                  {r.source === 'placeholder' ? 'Nadomesti z GPX…' : 'Zamenjaj z GPX…'}
                </Menu.Item>
                <Menu.Item onClick={() => setRenaming(r.id)}>Preimenuj</Menu.Item>
                <Menu.Item color="red" onClick={() => confirmDelete(r)}>
                  Izbriši
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : null}
        </div>
      ))}

      {routes.some((r) => r.source === 'placeholder') ? (
        <div
          style={{
            marginTop: 6,
            padding: '10px 11px',
            borderRadius: 8,
            background: 'oklch(0.99 0.012 75)',
            border: '1px solid oklch(0.93 0.03 75)',
            fontSize: rem(12),
            color: 'oklch(0.45 0.09 75)',
            lineHeight: 1.45,
          }}
        >
          Poenostavljene trase so ravne povezave med sektorji — uvozi datoteko GPX, da se nariše
          pravi potek proge.
        </div>
      ) : null}
    </>
  );
}

/** The course's line as the list shows it: its colour, thickened with it. */
function RouteSwatch({ route }: { route: Pick<RaceRoute, 'color' | 'weight'> }) {
  return (
    <span
      className="mp-dash"
      style={{ background: route.color, height: Math.min(Math.max(route.weight, 2), 8) }}
    />
  );
}

/** A CSS colour as `#rrggbb`, which is all a native colour input takes. */
function toHex(color: string): string {
  const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!ctx) return '#000000';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return `#${[r, g, b].map((n) => (n ?? 0).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Colour and thickness of one course. Both draw on the map while they are
 * picked; a swatch is saved on click, the slider when it is let go, and the
 * free colour when its picker closes.
 */
function RouteStylePicker({
  route,
  onDraft,
  onCommit,
}: {
  route: RaceRoute;
  onDraft: (style: RouteStyleDraft[string]) => void;
  onCommit: (patch: Pick<RoutePatch, 'color' | 'weight'>) => void;
}) {
  const commit = useRef(onCommit);
  useEffect(() => {
    commit.current = onCommit;
  });
  const [opened, setOpened] = useState(false);

  // React's onChange is the native `input` event, which fires on every move of
  // the picker; the save waits for `change`, sent when the picker closes.
  const saveOnClose = useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    const done = () => commit.current({ color: el.value });
    el.addEventListener('change', done);
    return () => el.removeEventListener('change', done);
  }, []);

  const inPalette = ROUTE_PALETTE.includes(route.color);
  const hex = useMemo(() => toHex(route.color), [route.color]);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" withArrow shadow="md" zIndex={1400}>
      <Popover.Target>
        <button
          className="mp-dashbtn"
          aria-label={`Barva in debelina trase ${route.name}`}
          title="Barva in debelina"
          onClick={() => setOpened((o) => !o)}
        >
          <RouteSwatch route={route} />
        </button>
      </Popover.Target>
      <Popover.Dropdown p={12} w={228}>
        <div className="mp-stylehead">Barva</div>
        <div className="mp-swatches">
          {ROUTE_PALETTE.map((c) => (
            <button
              key={c}
              className={`mp-swatch${route.color === c ? ' mp-on' : ''}`}
              style={{ background: c }}
              aria-label={`barva ${c}`}
              aria-pressed={route.color === c}
              onClick={() => onCommit({ color: c })}
            />
          ))}
          <label
            className={`mp-swatch mp-custom${inPalette ? '' : ' mp-on'}`}
            style={inPalette ? undefined : { background: route.color }}
            title="Poljubna barva"
          >
            <input
              ref={saveOnClose}
              type="color"
              value={hex}
              onChange={(e) => onDraft({ color: e.currentTarget.value })}
            />
          </label>
        </div>
        <div className="mp-stylehead" style={{ marginTop: 12 }}>
          Debelina <span className="mp-mono">{route.weight} px</span>
        </div>
        <Slider
          min={ROUTE_WEIGHT.min}
          max={ROUTE_WEIGHT.max}
          step={1}
          value={route.weight}
          label={null}
          onChange={(weight) => onDraft({ weight })}
          onChangeEnd={(weight) => onCommit({ weight })}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button className={`mp-sw${on ? ' mp-on' : ''}`} onClick={onClick}>
      <i />
    </button>
  );
}

function RoutePolylines({ route }: { route: RaceRoute }) {
  const lines: number[][][] =
    route.geojson.type === 'LineString'
      ? [route.geojson.coordinates as number[][]]
      : (route.geojson.coordinates as number[][][]);
  return (
    <>
      {lines.map((line, i) => (
        <Polyline
          key={i}
          positions={line.map(([lng, lat]) => [lat!, lng!] as [number, number])}
          pathOptions={{
            color: route.color,
            weight: route.weight,
            opacity: route.source === 'placeholder' ? 0.5 : 0.65,
            dashArray: route.source === 'placeholder' ? '7 6' : undefined,
          }}
        />
      ))}
    </>
  );
}

function SectorHull({ sector, count }: { sector: Sector; count: number }) {
  const center: [number, number] = [sector.lat!, sector.lng!];
  const label = useMemo(
    () =>
      L.divIcon({
        className: '',
        iconSize: [140, 18],
        iconAnchor: [70, 46],
        html: `<div class="mp-sector">${sector.name} · ${count}</div>`,
      }),
    [sector.name, count],
  );
  return (
    <>
      <Circle
        center={center}
        radius={900}
        pathOptions={{ color: 'oklch(0.55 0.03 245)', weight: 1, opacity: 0.28, fillOpacity: 0.04 }}
        interactive={false}
      />
      <Marker position={center} icon={label} interactive={false} />
    </>
  );
}

function PostPin({
  post,
  statusKey,
  fill,
  dashed,
  selected,
  showLabel,
  briefLayer,
  briefIsMissing,
  external,
  change,
  now,
  onClick,
}: {
  post: TimelinePost;
  statusKey: MapStatusKey;
  /** On race day: the slices of the people who matter now, instead of the status colour. */
  fill: string | null;
  /** A slot with nobody on it is among them. */
  dashed: boolean;
  selected: boolean;
  showLabel: boolean;
  briefLayer: boolean;
  briefIsMissing: boolean;
  external: boolean;
  change: PostChange | null;
  now: Date;
  onClick: () => void;
}) {
  const icon = useMemo(() => {
    const ring = post.urgent
      ? 'box-shadow: 0 0 0 3px oklch(0.57 0.15 25 / 0.28), 0 2px 5px oklch(0.3 0.02 90 / 0.35);'
      : '';
    const bmark = briefIsMissing && briefLayer ? '<span>✉</span>' : '';
    // The square that means "an outside group works here" (SPEC §1.1J). It sits
    // on top of the status colour rather than replacing it, and yields to the
    // envelope on a mixed post — a missing briefing is the actionable one, and
    // a post staffed only by a team has no briefing to miss anyway.
    const emark = !bmark && external ? '<span class="mp-psq"></span>' : '';
    const chgdot = change
      ? `<div class="mp-chgdot" title="${changeLabel(change, now)}" style="background:${change.breaking ? 'oklch(0.6 0.16 30)' : 'oklch(0.78 0.11 75)'}"></div>`
      : '';
    return L.divIcon({
      className: `mp-pin${selected ? ' mp-sel' : ''}${showLabel ? '' : ' mp-hidelabel'}`,
      html: `<div class="mp-pbody${dashed ? ' mp-pdashed' : ''}" style="background:${fill ?? MAP_COL[statusKey]};${ring}">${bmark}${emark}</div>${chgdot}<div class="mp-plabel">${post.code}</div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 26],
    });
  }, [
    post.urgent,
    post.code,
    briefIsMissing,
    briefLayer,
    external,
    change,
    now,
    selected,
    showLabel,
    statusKey,
    fill,
    dashed,
  ]);

  return (
    <Marker
      position={[post.lat!, post.lng!]}
      icon={icon}
      riseOnHover
      title={`${post.code} — ${post.micro}`}
      eventHandlers={{ click: onClick }}
    />
  );
}

function PostDetail({
  post,
  statusKey,
  live,
  vols,
  sectorName,
  coverKey,
  routeRef,
  onClose,
}: {
  post: TimelinePost;
  statusKey: MapStatusKey;
  /**
   * On race day: check-ins and the minute shown, so each shift says where it
   * stands; `now` when that minute is the clock's, the only time a check-in
   * may be recorded — the server stamps it with its own clock.
   */
  live: { cm: Map<string, ShiftCheckins>; minute: number; now: boolean } | null;
  vols: VolunteerLookup;
  sectorName: string;
  /** Cover photo of the post, if it has one. */
  coverKey: string | null;
  routeRef: PostRouteRef | null;
  onClose: () => void;
}) {
  const statusLabel = {
    ok: 'polno pokrito',
    part: 'čaka potrditev',
    gap: 'vrzel',
    none: 'brez izmen',
    brief: 'navodila še niso poslana',
    ekipa: 'zunanja ekipa',
    off: 'zunaj izmene',
  }[statusKey];

  const shifts = [...post.shifts].sort((a, b) => a.fromMin - b.fromMin);
  const liveViews = live ? liveViewsOf(post, live.cm, live.minute) : null;
  const { can } = useSession();
  const checkin = useCheckinMutations();

  return (
    <div className="mp-float mp-detail">
      <button className="mp-dx" onClick={onClose}>
        ×
      </button>
      {coverKey ? (
        <img
          className="mp-cover"
          src={photoUrl(coverKey)}
          alt={`Fotografija mesta ${post.code}`}
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="mp-mono" style={{ fontSize: rem(15), fontWeight: 700 }}>
          {post.code}
        </span>
        {post.urgent ? (
          <span
            style={{
              fontSize: rem(11),
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: 'oklch(0.55 0.12 25)',
            }}
          >
            NUJEN
          </span>
        ) : null}
        <span
          style={{
            fontSize: rem(11.5),
            padding: '1px 7px',
            borderRadius: 20,
            background: 'oklch(0.968 0.005 95)',
            color: 'oklch(0.45 0.012 90)',
          }}
        >
          {post.type}
        </span>
      </div>
      <div style={{ fontSize: rem(13.5), color: 'oklch(0.4 0.012 90)', marginTop: 4, lineHeight: 1.35 }}>
        {post.micro}
      </div>
      <div style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)', marginTop: 2 }}>
        {sectorName}
        {live ? '' : ` · ${statusLabel}`}
      </div>
      <div className="mp-mono" style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)', marginTop: 8 }}>
        {post.lat!.toFixed(5)}, {post.lng!.toFixed(5)}
      </div>
      {routeRef ? (
        <div
          style={{
            fontSize: rem(12),
            marginTop: 4,
            color: isOnCourse(routeRef) ? 'oklch(0.45 0.012 90)' : 'oklch(0.5 0.13 25)',
          }}
        >
          {isOnCourse(routeRef) ? (
            <>
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 3,
                  borderRadius: 2,
                  background: routeRef.routeColor,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              {describeRouteRef(routeRef)}
            </>
          ) : (
            describeRouteRef(routeRef)
          )}
        </div>
      ) : null}
      <div
        style={{ marginTop: 10, fontSize: rem(11.5), letterSpacing: '0.04em', color: 'oklch(0.55 0.012 90)' }}
      >
        IZMENE
      </div>
      {live && shifts.length ? (
        shifts.map((s) => (
          <LiveShiftRow
            key={s.id}
            shift={s}
            vols={vols}
            cm={live.cm}
            view={liveViews!.get(s.id)!}
            minute={live.minute}
            onRecord={can('live:act') && live.now ? (kind) => checkin.set.mutate({ shiftId: s.id, kind }) : undefined}
            busy={checkin.set.isPending}
          />
        ))
      ) : shifts.length ? (
        shifts.map((s) => {
          const v = s.volunteerId ? vols.get(s.volunteerId) : undefined;
          const free = !s.volunteerId || s.state === 'open';
          // The board's own reading of the shift, so this row agrees with the
          // block on Časovnica and with the pin the panel was opened from: a team
          // and a finished shift are settled (SPEC §1.1J), an answer is this
          // shift's and not the person's (SPEC §1.1F).
          const standing = blockStatus(s, v);
          const dotColor = free
            ? MAP_COL.gap
            : standing === 'potrjen' || standing === 'opravljeno' || standing === 'zunanja'
              ? MAP_COL.ok
              : standing === 'zavrnjen'
                ? MAP_COL.gap
                : MAP_COL.part;
          // The same question the pin's "navodila še niso poslana" mark asks.
          const stale = v !== undefined && standing === 'potrjen' && briefingStale(v);
          return (
            <div key={s.id} className="mp-srow">
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: dotColor,
                  flexShrink: 0,
                }}
              />
              <span className="mp-mono" style={{ fontSize: rem(11.5), fontWeight: 600 }}>
                {minutesToHm(s.fromMin)}–{minutesToHm(s.toMin)}
              </span>
              <span
                style={{
                  fontSize: rem(12.5),
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {free ? 'PROSTO' : (v?.name ?? '?')}
              </span>
              {stale ? (
                <span title="brifing ni poslan" style={{ fontSize: rem(12), color: 'oklch(0.55 0.1 75)' }}>
                  ✉
                </span>
              ) : null}
            </div>
          );
        })
      ) : (
        <div style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)', marginTop: 5 }}>Ni izmen.</div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        {/* Mesta opens on the day the header has selected, which is this map's day. */}
        <Link className="mp-dlink" to={`/mesta?post=${post.id}`}>
          Podrobnosti mesta
        </Link>
        <a
          className="mp-dlink"
          style={{ flex: 'none', padding: '8px 12px' }}
          href={`https://www.openstreetmap.org/?mlat=${post.lat}&mlon=${post.lng}#map=17/${post.lat}/${post.lng}`}
          target="_blank"
          rel="noreferrer"
        >
          Navigacija ↗
        </a>
      </div>
    </div>
  );
}

/** One shift on race day: who, until when, where they stand, their number, and a check-in for them. */
function LiveShiftRow({
  shift: s,
  vols,
  cm,
  view,
  minute,
  onRecord,
  busy,
}: {
  shift: Shift;
  vols: VolunteerLookup;
  cm: Map<string, ShiftCheckins>;
  /** The shift within its person's stretch, as the pin reads it. */
  view: LiveShiftView;
  minute: number;
  /** Absent for a login that may only watch. */
  onRecord?: (kind: 'in' | 'out') => void;
  busy: boolean;
}) {
  const v = s.volunteerId ? vols.get(s.volunteerId) : undefined;
  const ext = v !== undefined && isExternal(v);
  const free = !holdsPlace(s);
  const chip = liveChip(s, cm, minute, ext, view);
  const may = checkinActions(s, view, minute, ext);
  return (
    <div className="mp-srow mp-srow--live">
      <span className="mp-mono" style={{ fontSize: rem(11.5), fontWeight: 600 }}>
        {minutesToHm(s.fromMin)}–{minutesToHm(s.toMin)}
      </span>
      <span className="mp-srow-name">
        {free ? 'PROSTO' : (v?.name ?? '?')}
        {v?.phone && !ext && !free ? (
          <a className="mp-srow-tel" href={`tel:${v.phone}`}>
            {v.phone}
          </a>
        ) : null}
      </span>
      {chip ? <span className={`mp-att-chip mp-att-chip--${chip.status}`}>{chip.text}</span> : null}
      {onRecord && (may.in || may.out) ? (
        <>
          {may.in ? (
            <button type="button" className="mp-chip" disabled={busy} onClick={() => onRecord('in')}>
              Prijavi prihod
            </button>
          ) : null}
          {may.out ? (
            <button type="button" className="mp-chip" disabled={busy} onClick={() => onRecord('out')}>
              Odjavi
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const LIVE_RANK_ORDER: readonly LiveStatus[] = ['prosto', 'zamuja', 'brez', 'ni'];

/** The ISO date after `iso`. */
function nextIsoDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
